import React from 'react'
import { ColumnDefinition, ColumnGroupDefinition, TableRowData } from '../types'
import { WorkPeriodWithMission, WorkPeriodWithSummary } from '../../../../api/work-periods'
import { formatDay, getWeekdayMondayBased } from '../../../../utils/date'
import { createColumns } from '../utils'
import { WeekSummary } from '../../../../../../backend/src/services/resources/missions/missions.summary.service.types'
import { getDay } from 'date-fns'
import Protected, { isAuthorizedFor } from '../../../protected/protected'
import WorkPeriodValidateButton from '../../../work-periods/validate-button'
import WorkPeriodLink from '../../../work-periods/work-period-link'
import {
  getEmployerRuleFields,
  getWorkPeriodFields,
  getWorkPeriodMetaFields,
} from '../../../work-periods/table-field-definitions'
import { getDocIdAsString } from '../../../../utils/documents'
import { Element, Icon } from 'react-bulma-components'
import { getWorkPeriodFromDayDate } from '../../../../utils/work-periods'
import { UserRemoveIcon } from '@heroicons/react/outline'
import { SpecialPeriod } from '../../../../api/special-periods'

interface DayColumnsGroupProps {
  day: Date
  dayIndex: number
  handleValidation?: (workPeriod: WorkPeriodWithMission) => void
  hideActions?: boolean
  minimalMode?: boolean
  employerRules: WeekSummary['employerRules']
  isDayWithoutWorkPeriods?: boolean
  specialPeriods: SpecialPeriod[]
}

/**
 * Utility function to safely get work period data
 * @param data - The row data containing mission information
 * @param dayIndex - The index of the day to get work period for
 * @returns The work period if it exists, null otherwise
 */
const getWorkPeriod = (data: TableRowData, day: Date): WorkPeriodWithSummary | null => {
  const workPeriod = getWorkPeriodFromDayDate(data.mission.workPeriods, day) as
    | WorkPeriodWithSummary
    | undefined
  return workPeriod || null
}

/**
 * Creates a render function for work period cells.
 *
 * For employer members the cell is wrapped in a WorkPeriodLink that handles
 * both real and virtual (template-based) work periods. Virtual WPs are
 * materialized on click before navigating to the detail page.
 *
 * @param day - The calendar day this column represents
 * @param renderFn - Function to render the cell content
 * @returns A render function for the cell
 */
const renderWorkPeriodCell = (
  day: Date,
  renderFn: (workPeriod: WorkPeriodWithSummary, data: any) => React.ReactNode,
) => {
  return (data: TableRowData) => {
    const workPeriod = getWorkPeriod(data, day)

    if (!workPeriod) return null

    const strikethroughStyle =
      workPeriod.isCancelled || workPeriod.isAbandoned
        ? { textDecoration: 'line-through', textDecorationThickness: '1px' }
        : {}

    // Employer members get a clickable link (handles virtual WP materialization)
    if (isAuthorizedFor(['employerMember'])) {
      return (
        <WorkPeriodLink workPeriod={workPeriod} textColor="dark" style={strikethroughStyle}>
          <Element renderAs="span">{renderFn(workPeriod, data)}</Element>
        </WorkPeriodLink>
      )
    }

    // Other roles: plain text, no link
    return (
      <Element renderAs="span" textColor="dark" style={strikethroughStyle}>
        <Element renderAs="span">{renderFn(workPeriod, data)}</Element>
      </Element>
    )
  }
}

const createWorkPeriodColumnsGroup = ({
  day,
  dayIndex,
  handleValidation,
  hideActions,
  minimalMode,
  employerRules,
  isDayWithoutWorkPeriods,
  specialPeriods,
}: DayColumnsGroupProps): ColumnGroupDefinition => {
  const specialPeriodsNames = specialPeriods?.map(specialPeriod => specialPeriod.name).join(', ')
  const header = specialPeriodsNames ? `${formatDay(day)} (${specialPeriodsNames})` : formatDay(day)

  const buildClassName = (className = '') => {
    if (specialPeriodsNames) {
      className += ' has-background-primary-light'
    }
    return className
  }

  const employerRulesColumns: ColumnDefinition[] = getEmployerRuleFields(employerRules).map(
    field => ({
      header: field.header,
      id: `day-${dayIndex}-${field.id}`,
      isHidden: minimalMode,
      className: buildClassName(),
      render: data => {
        const workPeriod = getWorkPeriod(data, day)
        const weekDayNumber = getWeekdayMondayBased(getDay(day))
        const rule = field.employerRule
        const value =
          rule.kind === 'goal'
            ? (data.totals.days[weekDayNumber]?.employerData.goals[getDocIdAsString(rule.ruleId)] ??
              null)
            : (data.totals.days[weekDayNumber]?.employerData.durations[
                getDocIdAsString(rule.ruleId)
              ] ?? null)
        if (!workPeriod && !value) return null
        return field.render(workPeriod, value)
      },
    }),
  )

  if (isDayWithoutWorkPeriods) {
    return {
      Header: formatDay(day),
      id: `day-${dayIndex}`,
      columns: createColumns([
        {
          header: (
            <Element>
              <Icon
                data-tooltip="Jour sans périodes de travail"
                size="small"
                color="grey"
                style={{ marginRight: '2.0rem' }}
              >
                <UserRemoveIcon />
              </Icon>
            </Element>
          ),
          id: `day-${dayIndex}-no-work-periods`,
          className: 'table-section-start',
        },
      ]),
    }
  }

  const dataFieldColumns: ColumnDefinition[] = getWorkPeriodFields().map((field, fieldIndex) => ({
    header: field.header,
    id: `day-${dayIndex}-${field.id}`,
    isHidden: field.minimalOnly && minimalMode,
    className: buildClassName(fieldIndex === 0 ? 'table-section-start' : ''),
    render: renderWorkPeriodCell(day, workPeriod => field.render(workPeriod)),
  }))

  const metaColumns: ColumnDefinition[] = getWorkPeriodMetaFields().map(field => ({
    header: field.header,
    id: `day-${dayIndex}-${field.id}`,
    isHidden: field.minimalOnly && minimalMode,
    className: buildClassName(),
    render: renderWorkPeriodCell(day, workPeriod => field.render(workPeriod)),
  }))

  return {
    Header: header,
    id: `day-${dayIndex}`,
    columns: createColumns([
      ...dataFieldColumns,
      ...employerRulesColumns,
      ...metaColumns,
      {
        header: 'Valider',
        id: `day-${dayIndex}-validated`,
        isHidden: hideActions,
        className: buildClassName(),
        render: renderWorkPeriodCell(day, (workPeriod, data) => (
          <Protected
            roles={['employerMember']}
            action={{ resource: 'missions', name: 'validateWorkPeriod' }}
          >
            <Element renderAs="span" onClick={e => e.preventDefault()}>
              <WorkPeriodValidateButton
                workPeriod={workPeriod}
                label=""
                type="button"
                size="smallest"
                onClick={workPeriod =>
                  handleValidation &&
                  handleValidation({
                    ...workPeriod,
                    mission: { ...data.mission, _id: getDocIdAsString(workPeriod.mission) },
                  })
                }
              />
            </Element>
          </Protected>
        )),
      },
    ]),
  }
}

export default createWorkPeriodColumnsGroup
